/**
 * How to connect an AI tool to the MCP server: one tab per client, Sign in first (the tool signs in
 * through Wynxx and the person allows it), then the personal-token way where the client takes one.
 * Snippets hold placeholders, never a secret.
 */

import { useSession } from '@/session/SessionContext';
import { CopyButton } from '@/ui/CopyButton';
import { GuideDialog } from '@/ui/GuideDialog';

import {
  CHATGPT_STEPS,
  CLAUDE_CODE_SIGN_IN_NEXT,
  CONNECTOR_STEPS,
  CONNECTOR_TOKEN_NOTE,
  MENU_NAMES_VARY,
  claudeCodeSignInCommand,
  claudeCodeTokenCommand,
  mcpUrl,
} from './connectSnippets';

function Address({ id, url }: { id: string; url: string }) {
  return (
    <div className="field">
      <label htmlFor={id}>MCP address</label>
      <div className="token-row">
        <input id={id} className="mono-input" readOnly value={url} onFocus={(e) => e.currentTarget.select()} />
        <CopyButton text={url} label="Copy address" />
      </div>
    </div>
  );
}

function Command({ id, label, copyLabel, text }: { id: string; label: string; copyLabel: string; text: string }) {
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <textarea id={id} className="token-snippet" readOnly rows={3} value={text} />
      <div>
        <CopyButton text={text} label={copyLabel} />
      </div>
    </div>
  );
}

function Steps({ items }: { items: string[] }) {
  return (
    <ol className="connect-steps">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ol>
  );
}

const SIGN_IN = 'Sign in (recommended)';
const WITH_TOKEN = 'With a personal token';

function ClaudeCodePane({ url }: { url: string }) {
  return (
    <div className="stack-md">
      <Address id="connect-code-url" url={url} />
      <h4>{SIGN_IN}</h4>
      <Command id="connect-code-signin" label="Claude Code command" copyLabel="Copy command" text={claudeCodeSignInCommand(url)} />
      <p className="hint">{CLAUDE_CODE_SIGN_IN_NEXT}</p>
      <h4>{WITH_TOKEN}</h4>
      <p className="hint">Create a token on this page, then run this in a terminal with your token in place of {'<your token>'}.</p>
      <Command id="connect-code-token" label="Claude Code command with a token" copyLabel="Copy token command" text={claudeCodeTokenCommand(url)} />
    </div>
  );
}

function ConnectorPane({ id, url }: { id: string; url: string }) {
  return (
    <div className="stack-md">
      <Address id={`${id}-url`} url={url} />
      <h4>{SIGN_IN}</h4>
      <Steps items={CONNECTOR_STEPS} />
      <p className="hint">{MENU_NAMES_VARY}</p>
      <h4>{WITH_TOKEN}</h4>
      <p className="hint">{CONNECTOR_TOKEN_NOTE}</p>
    </div>
  );
}

function ChatGptPane({ url }: { url: string }) {
  return (
    <div className="stack-md">
      <Address id="connect-chatgpt-url" url={url} />
      <h4>{SIGN_IN}</h4>
      <Steps items={CHATGPT_STEPS} />
      <p className="hint">{MENU_NAMES_VARY} ChatGPT connectors sign in only, so there is no personal-token way here.</p>
    </div>
  );
}

export function ConnectDialog({ onClose }: { onClose: () => void }) {
  const url = mcpUrl(useSession().mcpUrl);
  return (
    <GuideDialog
      title="How to connect"
      subtitle="Point an AI tool at the MCP server. It reads Wynxx Discover as you, limited to what you allow."
      label="Client"
      onClose={onClose}
      tabs={[
        { key: 'claude-code', name: 'Claude Code', panel: <ClaudeCodePane url={url} /> },
        { key: 'claude-desktop', name: 'Claude Desktop', panel: <ConnectorPane id="connect-desktop" url={url} /> },
        { key: 'claude-web', name: 'Claude (web)', panel: <ConnectorPane id="connect-web" url={url} /> },
        { key: 'chatgpt', name: 'ChatGPT', panel: <ChatGptPane url={url} /> },
      ]}
    />
  );
}
